import type {
  UseCashbackCampaignsContext,
  UseTourOperatorDiscountCampaignsContext,
  TourOperatorDiscountCampaign,
  CashbackCampaign,
  UseCombinedCampaignsContext,
  CombinedCampaign,
} from '@/modules/common/hooks/useCampaigns/types';
import type {Hotel} from '@/redux/v1/hotels/list/models';
import type {Meta} from '@/redux/v1/models';
import type {Offer} from '@/redux/v1/offers/list/models';

import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {DEFAULT_REQUEST_TIMEOUT, INVIA_TYPE_DISCOUNT} from '@/consts';
import {useQueryParams} from '@/modules/common/context/QueryParams';
import {
  useSeaCampaign,
  SEA_CAMPAIGN_URL_PARAM,
} from '@/modules/common/context/SeaCampaign';
import {
  tourOperatorDiscountCampaignContext,
  cashbackCampaignContext,
  combinedCampaignContext,
} from '@/modules/common/hooks/useCampaigns/contexts';
import {requestCampaigns} from '@/modules/common/hooks/useCampaigns/requestCampaigns';
import {
  getFilteredCampaigns,
  getTourOperatorsBySpecial,
  graphQLResponseToCashbackCampaign,
  graphQLResponseToCombinedCampaign,
  graphQLResponseToTourOperatorDiscountCampaign,
} from '@/modules/common/hooks/useCampaigns/utils';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {EVENT_FILTER_SET} from '@/redux/middleware/googleTagManager';

type Props = React.PropsWithChildren<{
  metaFiltered?: null | Meta;
  metaUnfiltered?: null | Meta;
}>;

export const CampaignsProvider = ({
  children,
  metaFiltered,
  metaUnfiltered,
}: Props) => {
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [tourOperatorDiscountCampaign, setTourOperatorDiscountCampaign] =
    useState<null | TourOperatorDiscountCampaign>(null);
  const [cashbackCampaign, setCashbackCampaign] =
    useState<null | CashbackCampaign>(null);
  const [combinedCampaign, setCombinedCampaign] =
    useState<null | CombinedCampaign>(null);
  const seaCampaign = useSeaCampaign();
  const [queryParams, setQueryParams] = useQueryParams();
  const travelType = queryParams.travelType;

  useEffect(() => {
    setIsLoadingCampaigns(true);
    const controller = new AbortController();

    (async () => {
      try {
        const response = await requestCampaigns(controller.signal, travelType);

        if (response.data.data.tour_operator_discount_campaign.length > 0) {
          setTourOperatorDiscountCampaign(
            graphQLResponseToTourOperatorDiscountCampaign(
              response.data.data.tour_operator_discount_campaign
            )[0]
          );
        }

        const sortedCashbackCampaigns = graphQLResponseToCashbackCampaign(
          response.data.data.cashback_campaign
        ).sort((a, b) => Number(b.seasonal) - Number(a.seasonal));

        if (sortedCashbackCampaigns[0]) {
          setCashbackCampaign(sortedCashbackCampaigns[0]);

          if (response.data.data.cashback_discount_campaign.length > 0) {
            setCombinedCampaign(
              graphQLResponseToCombinedCampaign(
                response.data.data.cashback_discount_campaign,
                sortedCashbackCampaigns[0].gradient
              )[0]
            );
          }
        }
      } catch {
        setTourOperatorDiscountCampaign(null);
        setCashbackCampaign(null);
        setCombinedCampaign(null);
      }
      setIsLoadingCampaigns(false);
    })();

    window.setTimeout(() => {
      controller.abort();
    }, DEFAULT_REQUEST_TIMEOUT);

    return () => {
      controller.abort();
    };
  }, [travelType]);

  const dispatch = useDispatch();
  const {dispatchGenericTrackingEvent} = useTracking();

  const getAssociatedDiscountCampaign = useCallback(
    (hotelOrOffer?: null | Hotel | Offer) => {
      const hasSpecial = Boolean(
        hotelOrOffer?.tourOperatorSpecials?.specials?.find(
          (o: {type: string}) => o.type === INVIA_TYPE_DISCOUNT
        )
      );

      return hasSpecial ? tourOperatorDiscountCampaign : null;
    },
    [tourOperatorDiscountCampaign]
  );

  const currentPage = useCurrentPage();

  const handleTourOperatorDiscountCampaignBannerClick = useCallback(
    (campaign: TourOperatorDiscountCampaign) => {
      dispatchGenericTrackingEvent({
        event: 'select_promotion',
        ecommerce: {
          creative_name: campaign.titles.lg,
          promotion_name: 'Last Minute',
        },
        event_action: 'promotion_banner_click_last_minute',
        non_interaction: false,
      });

      const nextSpecials = Array.from(
        new Set((queryParams.specials ?? []).concat(INVIA_TYPE_DISCOUNT))
      );
      const nextTourOperators = Array.from(
        new Set(
          (queryParams.tourOperators ?? []).concat(campaign.tourOperators)
        )
      );
      dispatch({
        type: EVENT_FILTER_SET,
        payload: {filters: [['specials', nextSpecials]], currentPage},
      });
      dispatch({
        type: EVENT_FILTER_SET,
        payload: {filters: [['tourOperator', nextTourOperators]], currentPage},
      });
      setQueryParams(
        (previous) => ({
          ...previous,
          specials: nextSpecials,
          tourOperators: nextTourOperators,
          [SEA_CAMPAIGN_URL_PARAM]: null,
        }),
        {
          replace: !seaCampaign.active,
        }
      );
    },
    [
      currentPage,
      dispatch,
      dispatchGenericTrackingEvent,
      queryParams.specials,
      queryParams.tourOperators,
      seaCampaign.active,
      setQueryParams,
    ]
  );

  const tourOperatorDiscountCampaignContextValue =
    useMemo<UseTourOperatorDiscountCampaignsContext>(() => {
      if (!tourOperatorDiscountCampaign) {
        return {
          filterInterceptorContent: {highlight: '', text: ''},
          isLoadingCampaigns,
          shouldShowBanner: false,
          shouldShowFilter: false,
          campaign: null,
          getAssociatedDiscountCampaign,
          handleTourOperatorDiscountCampaignBannerClick,
        };
      }

      const filteredCampaignsByMeta = getFilteredCampaigns(
        tourOperatorDiscountCampaign,
        getTourOperatorsBySpecial(metaFiltered)
      );
      const filteredCampaignsByUnfilteredMeta = getFilteredCampaigns(
        tourOperatorDiscountCampaign,
        getTourOperatorsBySpecial(metaUnfiltered)
      );

      const text = (
        tourOperatorDiscountCampaign.filterInterceptorContent ?? ''
      ).split(' ');
      const highlight = text.pop() ?? '';

      const filterInterceptorContent = {
        text: text.join(' '),
        highlight,
      };

      if (filteredCampaignsByMeta) {
        return {
          filterInterceptorContent,
          isLoadingCampaigns,
          shouldShowBanner: true,
          shouldShowFilter: Boolean(filteredCampaignsByUnfilteredMeta),
          campaign: filteredCampaignsByMeta,
          getAssociatedDiscountCampaign,
          handleTourOperatorDiscountCampaignBannerClick,
        };
      }

      return {
        filterInterceptorContent,
        isLoadingCampaigns,
        shouldShowBanner: false,
        shouldShowFilter: Boolean(filteredCampaignsByUnfilteredMeta),
        campaign: null,
        getAssociatedDiscountCampaign,
        handleTourOperatorDiscountCampaignBannerClick,
      };
    }, [
      tourOperatorDiscountCampaign,
      metaUnfiltered,
      metaFiltered,
      handleTourOperatorDiscountCampaignBannerClick,
      getAssociatedDiscountCampaign,
      isLoadingCampaigns,
    ]);

  const cashbackCampaignContextValue =
    useMemo<UseCashbackCampaignsContext>(() => {
      return !isLoadingCampaigns && cashbackCampaign
        ? {
            shouldShowBanner: true,
            campaign: cashbackCampaign,
            isLoadingCampaigns,
          }
        : {
            shouldShowBanner: false,
            campaign: null,
            isLoadingCampaigns,
          };
    }, [cashbackCampaign, isLoadingCampaigns]);

  const combinedCampaignContextValue =
    useMemo<UseCombinedCampaignsContext>(() => {
      if (combinedCampaign && cashbackCampaignContextValue.shouldShowBanner) {
        if (tourOperatorDiscountCampaignContextValue.shouldShowBanner) {
          return {
            combinedCampaign,
            discountCampaign: tourOperatorDiscountCampaignContextValue.campaign,
            isLoadingCampaigns,
            handleTourOperatorDiscountCampaignBannerClick,
            shouldShowBanner: true,
            shouldShowInterceptor: true,
          };
        } else if (tourOperatorDiscountCampaign) {
          return {
            combinedCampaign,
            discountCampaign: tourOperatorDiscountCampaign,
            isLoadingCampaigns,
            handleTourOperatorDiscountCampaignBannerClick,
            shouldShowBanner: false,
            shouldShowInterceptor: true,
          };
        }
      }

      return {
        shouldShowBanner: false,
        shouldShowInterceptor: false,
        combinedCampaign: null,
        discountCampaign: null,
        isLoadingCampaigns,
        handleTourOperatorDiscountCampaignBannerClick,
      };
    }, [
      cashbackCampaignContextValue.shouldShowBanner,
      combinedCampaign,
      handleTourOperatorDiscountCampaignBannerClick,
      isLoadingCampaigns,
      tourOperatorDiscountCampaign,
      tourOperatorDiscountCampaignContextValue.campaign,
      tourOperatorDiscountCampaignContextValue.shouldShowBanner,
    ]);

  return (
    <combinedCampaignContext.Provider value={combinedCampaignContextValue}>
      <cashbackCampaignContext.Provider value={cashbackCampaignContextValue}>
        <tourOperatorDiscountCampaignContext.Provider
          value={tourOperatorDiscountCampaignContextValue}
        >
          {children}
        </tourOperatorDiscountCampaignContext.Provider>
      </cashbackCampaignContext.Provider>
    </combinedCampaignContext.Provider>
  );
};
